/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Input,
  Row,
  Col,
  Spin,
  Card,
  Tabs,
  Button,
  message,
} from 'antd';
import {
  CalculatorOutlined,
  ImportOutlined,
  DiffOutlined,
} from '@ant-design/icons';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import getFormatedValues from 'utils/formatValues';
import NumericInput from 'components/NumericInput';
import Select from 'components/Select';
import Editor from 'components/Editor/index';
import PlainTransfer from 'components/PlainTransfer';
import { createDecimalRegExp } from 'utils/patterns';
import { onError } from 'utils/handlers';
import CreateClave from 'utils/createClave';
import {
  getTiposDeRecargos,
  postTipoDeRecargo,
  patchTipoDeRecargo,
  deleteTipoDeRecargo,
  getTipoDeRecargo,
} from 'api/recaudacion/tiposDeRecargo';
import { getTiposDeAplicacion, getPeriodos } from 'api/recaudacion/index';
import { getConceptoDeIngresos, getConceptosDeIngresos } from 'api/catalogos/conceptosDeIngresos';
import { getCatalogoDeTarifas } from 'api/configuracion/catalogoDeTarifas';
import { getClase, getClases } from 'api/catalogos/clases';
import { getTiposDeDescuentos } from 'api/recaudacion/tiposDeDescuento';
import { getVariables } from 'api/configuracion/variables';
import { hasActionPermission, ActionsNames } from 'utils/permission/index';

const { TextArea } = Input;
const { TabPane } = Tabs;

const catalogsModels = [
  'catalogodetarifa',
  'clase',
  'conceptodeingreso',
  'periodo',
  'tipodeaplicacionparacargo',
  'tipodedescuento',
  'variable',
];

const TipoDeRecargo = ({ permission }) => {
  const monedaDefault = useSelector(({ auth }) => auth.entidad?.moneda);
  const [decimal] = createDecimalRegExp(monedaDefault);
  const [formGeneral] = Form.useForm();
  const [formCalculo] = Form.useForm();
  const [formAccesorios] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const tiposDeAplicacion = useSelector(({ catalogs }) => catalogs.tiposDeAplicacion)
    .map((e) => (e.id === 2 || e.id === 4 ? ({ ...e, disabled: true }) : e))
    .filter((e) => !e.disabled);
  const [periodos, setPeriodos] = useState([]);
  const [tarifas, setTarifas] = useState([]);
  const [tiposDescuentos, setTiposDescuentos] = useState([]);
  const [app, setApp] = useState(0);
  const [currentActiveTabKey, setCurrentTabKey] = useState('general');
  const [variables, setVariables] = useState([]);
  const [porPeriodo, setPorPeriodo] = useState(false);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [clasesTemp, setClasesTemp] = useState({ results: [] });
  const [conceptosDeIngresoTemp, setConceptosDeIngresoTemp] = useState({ results: [] });

  const criSelected = Form.useWatch('clase_cri', formGeneral);

  const clases = useRef({ results: [] });
  const conceptosDeIngreso = useRef({ results: [] });

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTiposDeRecargos();
    setData(_data);
    setLoading(false);
  };

  const onCancel = () => {
    setApp(0);
    setVisible(false);
    formGeneral.resetFields();
    formCalculo.resetFields();
    formAccesorios.resetFields();
    setSelectedRowKeys([]);
    setCurrentTabKey('general');
    setClasesTemp({ results: [] });
    setConceptosDeIngresoTemp({ results: [] });
  };

  useEffect(() => {
    let mounted = true;
    const validatePermission = () => {
      const _hasAllViewPermission = catalogsModels
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      await getTiposDeAplicacion();
      const dataClases = await getClases();
      const dataPeriodos = await getPeriodos();
      const dataCatTarifas = await getCatalogoDeTarifas();
      const dataVariables = await getVariables({ es_script: true });
      const dataDescuentos = await getTiposDeDescuentos({ aplicacion_del_descuento: 2 });

      if (mounted) {
        setClasesTemp(dataClases);
        setPeriodos(dataPeriodos);
        setTarifas(dataCatTarifas);
        setVariables(dataVariables);
        setTiposDescuentos(dataDescuentos);
      }

      await fetchData();
      setLoading(false);
    };

    validatePermission();
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onFinish = async () => {
    try {
      setLoading(true);
      await formGeneral.validateFields();
      await formCalculo.validateFields();
      await formAccesorios.validateFields();
      const values = {
        ...getFormatedValues(formCalculo.getFieldsValue()),
        ...formGeneral.getFieldsValue(),
        ...formAccesorios.getFieldsValue(),
      };
      values.dias_de_gracia = values.dias_de_gracia ? values.dias_de_gracia : 0;
      const [key] = selectedRowKeys;
      const saved = await (key ? patchTipoDeRecargo(key, values) : postTipoDeRecargo(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, [formGeneral, formCalculo], setCurrentTabKey);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteTipoDeRecargo(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    formGeneral.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
    CreateClave(data, formGeneral);
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const record = await getTipoDeRecargo(key);
    const _CRI = await getClase(record.clase_cri);
    const _concepto = await getConceptoDeIngresos(record.concepto_de_ingreso);
    setClasesTemp({ results: [_CRI] });
    setConceptosDeIngresoTemp({ results: [_concepto] });
    setApp(record.tipo_de_aplicacion);
    formGeneral.setFieldsValue(record);
    formAccesorios.setFieldsValue(record);
    formCalculo.setFieldsValue(record);
    setPorPeriodo(record.es_calculable_por_periodo);
    setLoading(false);
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const requiredRule = { required: true };

  const rules = {
    required: [requiredRule],
    porcentaje: [
      requiredRule,
      {
        validator: async (_, val) => {
          const parsedVal = parseFloat(val);
          if (val) {
            if (!decimal.test(val)) {
              throw new Error('Ingrese un valor decimal válido');
            } else if (parsedVal > 100) {
              throw new Error('El valor no puede ser mayor a 100');
            } else if (parsedVal <= 0) {
              throw new Error('El valor debe ser mayor a 0');
            }
          }
        },
      },
    ],
    porcentajemaximo: [
      requiredRule,
      {
        validator: async (_, val) => {
          const parsedVal = parseFloat(val);
          if (val) {
            if (!decimal.test(val)) {
              throw new Error('Ingrese un valor decimal válido');
            }
          } else if (parsedVal <= 0) {
            throw new Error('El valor debe ser mayor a 0');
          }
        },
      },
    ],
    importe: [
      requiredRule,
      {
        validator: async (_, val) => {
          if (val && !decimal.test(val)) {
            throw new Error('Ingrese un valor decimal válido');
          }
        },
      },
    ],
    importe_maximo: [
      requiredRule,
      {
        validator: async () => {
          const values = formCalculo.getFieldsValue(['importe_minimo', 'importe_maximo']);
          const importe_minimo = parseFloat(values.importe_minimo);
          const importe_maximo = parseFloat(values.importe_maximo);
          if (values.importe_maximo) {
            if (!decimal.test(values.importe_maximo)) {
              throw new Error('Ingrese un valor decimal válido');
            } else if (importe_maximo <= importe_minimo) {
              throw new Error('El campo debe ser mayor al Importe Mínimo');
            }
          }
        },
      },
    ],
    importe_minimo: [
      requiredRule,
      {
        validator: async () => {
          const values = formCalculo.getFieldsValue(['importe_minimo', 'importe_maximo']);
          const importe_minimo = parseFloat(values.importe_minimo);
          const importe_maximo = parseFloat(values.importe_maximo);
          if (values.importe_minimo) {
            if (!decimal.test(values.importe_minimo)) {
              throw new Error('Ingrese un valor decimal válido');
            } else if (importe_minimo >= importe_maximo) {
              throw new Error('El campo debe ser menor al Importe Máximo');
            }
          }
        },
      },
    ],
    variables: [
      {
        required: app === 5,
      },
    ],
    periodo: [
      {
        required: formGeneral.getFieldValue('es_calculable_por_periodo'),
      },
    ],
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 150,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 250,
    },
    {
      titleText: 'Periodo',
      dataIndex: 'periodo',
      key: 'periodo',
      width: 150,
      render: (val) => periodos.find((item) => item.id === val)?.descripcion,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => (estadosGlobales.find((item) => item.id === val)?.descripcion),
    },
  ];

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            cols={columns}
            data={data}
            handleOnRowClick={handleOnRowClick}
            rowSelection={rowSelection}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            loading={loading}
            rowKey="id"
            permission={permission}
            hasAllViewPermission={hasAllViewPermission}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipo de Recargo`}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
                permission={permission}
              />
            )}
            bordered={false}
          >
            <Tabs
              type="card"
              activeKey={currentActiveTabKey}
              onChange={setCurrentTabKey}
            >
              <TabPane
                tab={(
                  <span>
                    <ImportOutlined />
                    General
                  </span>
                )}
                key="general"
                forceRender
              >
                <Form
                  form={formGeneral}
                  name="general"
                  layout="vertical"
                  onFinish={() => onFinish()}
                  initialValues={{
                    estados_globales: 1,
                    es_recargo_sobre_base: true,
                    es_multa: false,
                    es_calculable_por_periodo: false,
                  }}
                >
                  <Row gutter={10}>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="clase_cri"
                        label="Cri"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select
                          dataSource={
                            clasesTemp.results?.length
                              ? clasesTemp : clases.current
                          }
                          labelProp="denominacion"
                          usingExternalDataSource={Boolean(clasesTemp.results?.length)}
                          onClear={() => {
                            formGeneral.resetFields(['concepto_de_ingreso']);
                            setConceptosDeIngresoTemp({ results: [] });
                          }}
                          onChange={() => formGeneral.resetFields(['concepto_de_ingreso'])}
                          pagination={{
                            getter: getClases,
                            setter: (dataSource) => {
                              clases.current = dataSource;
                            },
                            config: { qName: 'denominacion' },
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="concepto_de_ingreso"
                        label="Concepto de ingreso"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select
                          dataSource={
                            conceptosDeIngresoTemp.results?.length
                              ? conceptosDeIngresoTemp : conceptosDeIngreso.current
                          }
                          labelProp="descripcion"
                          usingExternalDataSource={Boolean(conceptosDeIngresoTemp.results?.length)}
                          onClear={() => {
                            formGeneral.resetFields(['concepto_de_ingreso']);
                            setClasesTemp({ results: [] });
                            setConceptosDeIngresoTemp({ results: [] });
                          }}
                          disabled={!formGeneral.getFieldValue('clase_cri')}
                          pagination={{
                            getter: getConceptosDeIngresos,
                            setter: (dataSource) => {
                              conceptosDeIngreso.current = dataSource;
                            },
                            config: { qName: 'descripcion' },
                            params: { cri_clase: criSelected },
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="clave"
                        label="Clave"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Input allowClear disabled={selectedRowKeys.length} />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={12}
                    >
                      <Form.Item
                        name="descripcion"
                        label="Descripción"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Input.TextArea allowClear autoSize={{ minRows: 3, maxRows: 5 }} />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={12}
                    >
                      <Form.Item
                        name="observaciones"
                        label="Observaciones"
                        hasFeedback
                      >
                        <TextArea
                          allowClear
                          autoSize={{ minRows: 3, maxRows: 5 }}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={6}
                    >
                      <Form.Item
                        name="es_calculable_por_periodo"
                        label="¿Calculable por Periodo?"
                        hasFeedback
                      >
                        <Select
                          trueFalse
                          onChange={setPorPeriodo}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={6}
                    >
                      <Form.Item
                        name="es_recargo_sobre_base"
                        label="Recargo sobre base"
                        hasFeedback
                      >
                        <Select trueFalse />
                      </Form.Item>
                    </Col>

                    <Col
                      xs={24}
                      sm={24}
                      md={6}
                    >
                      <Form.Item
                        name="es_multa"
                        label="¿Es Multa?"
                        hasFeedback
                      >
                        <Select trueFalse />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={6}
                    >
                      <Form.Item
                        name="recalculable_manual"
                        label="¿Solo recalcular en trámite?"
                        hasFeedback
                      >
                        <Select trueFalse />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={6}
                    >
                      <Form.Item
                        name="estados_globales"
                        label="Estado"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select
                          disabled={!selectedRowKeys.length}
                          dataSource={estadosGlobales}
                        />
                      </Form.Item>
                    </Col>
                    {porPeriodo && (
                      <Col
                        xs={24}
                        sm={24}
                        md={6}
                      >
                        <Form.Item
                          name="periodo"
                          rules={rules.periodo}
                          label="Periodo"
                          hasFeedback
                        >
                          <Select dataSource={periodos} />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                  <Form.Item>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <CalculatorOutlined />
                    Parámetros de Cálculo
                  </span>
                )}
                key="calculo"
                forceRender
              >
                <Form
                  form={formCalculo}
                  onFinish={() => onFinish()}
                  name="calculo"
                  layout="vertical"
                  initialValues={{ dias_de_gracia: 0 }}
                >
                  <Row gutter={10}>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <NumericInput
                        name="dias_de_gracia"
                        label="Días de gracia"
                        allowZero
                      />
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        rules={rules.required}
                        name="tipo_de_aplicacion"
                        label="Tipo de aplicación"
                        hasFeedback
                      >
                        <Select dataSource={tiposDeAplicacion} onChange={setApp} />
                      </Form.Item>
                    </Col>
                    {app === 1 && (
                      <>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <Form.Item
                            rules={rules.required}
                            name="tope_maximo_hasta_base"
                            label="Tope hasta base"
                            hasFeedback
                          >
                            <Select trueFalse />
                          </Form.Item>
                        </Col>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <NumericInput
                            name="porcentaje"
                            label="Porcentaje"
                            rules={rules.porcentaje}
                            decimal
                          />
                        </Col>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <NumericInput
                            name="importe_maximo"
                            label="Porcentaje máximo del recargo"
                            rules={rules.porcentajemaximo}
                            decimal
                          />
                        </Col>
                      </>
                    )}
                    {app === 3 && (
                      <>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <Form.Item
                            rules={rules.required}
                            name="tope_maximo_hasta_base"
                            label="Tope hasta base"
                            hasFeedback
                          >
                            <Select trueFalse />
                          </Form.Item>
                        </Col>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <NumericInput
                            name="importe"
                            label="Importe"
                            rules={rules.importe}
                            decimal
                          />
                        </Col>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <NumericInput
                            name="importe_maximo"
                            label="Importe máximo del recargo"
                            rules={rules.porcentajemaximo}
                            decimal
                          />
                        </Col>
                      </>
                    )}
                    {app === 5 && (
                      <Editor
                        form={formCalculo}
                        rules={rules.required}
                        recargo
                        variables={variables}

                      />
                    )}
                    {app === 6 && (
                      <>
                        <Col
                          xs={24}
                          sm={24}
                          md={8}
                        >
                          <Form.Item
                            name="catalogo_de_tarifa"
                            rules={rules.required}
                            label="Tarifa"
                            hasFeedback
                          >
                            <Select dataSource={tarifas} />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                    {app === 7 && (
                    <Editor
                      form={formCalculo}
                      rules={rules.required}
                      recargo
                      variables={variables}
                    />
                    )}
                  </Row>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <DiffOutlined />
                    Accesorios
                  </span>
                )}
                key="accesorios"
              >
                <Form
                  layout="vertical"
                  form={formAccesorios}
                  onFinish={() => onFinish()}
                  name="accesorios"
                >
                  <Col span={24}>
                    <PlainTransfer
                      dataSource={tiposDescuentos}
                      label="Tipo de descuento"
                      formItemName="tipos_de_descuento"
                      form={formAccesorios}
                      filterProp="descripcion"
                    />
                  </Col>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
            </Tabs>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            setSelectedRowKeys([]);
          }}
          visible={visibleAlert}
          content={`Tipo de Recargo ${formGeneral.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default TipoDeRecargo;
